import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DropdownMenu,
  GhostIconButton,
  MenuItem,
  MenuSection,
  Modal,
} from "@certn/cascadia-react";
import { PrimaryButton, Text } from "@certn/cascadia";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { ReactivateDeactivateModal } from "./ReactivateDeactivateModal";

interface ActionsDropdownProps {
  templateIdsBelongingToActivePackages: string[];
  numActiveTemplates: number;
  mutate: () => void;
  templateId: string;
  name: string;
  isActive: boolean;
  hideDropDownOnEditPage?: boolean;
  settingsPathPrefix: string;
  apiPathPrefix: string;
}

export function ActionsDropdown({
  templateIdsBelongingToActivePackages,
  numActiveTemplates,
  mutate,
  templateId,
  name,
  isActive,
  hideDropDownOnEditPage,
  settingsPathPrefix,
  apiPathPrefix,
}: ActionsDropdownProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);

  const isInActivePackage =
    templateIdsBelongingToActivePackages.includes(templateId);

  const canDeactivate = !isInActivePackage && numActiveTemplates > 1;

  const isAlreadyOnEditPage = pathname.includes(
    `/settings/${settingsPathPrefix}/${templateId}`
  );

  return (
    <>
      {!hideDropDownOnEditPage && (
        <DropdownMenu
          triggerButton={
            <GhostIconButton
              testID={`actions-dropdown-${templateId}`}
              size="small"
              icon="more_horiz"
              aria-label={t("Expand menu")}
            />
          }
          placement="bottom right"
          onItemAction={(key) => {
            switch (key) {
              case "edit":
                navigate(`/settings/${settingsPathPrefix}/${templateId}`);
                break;
              case "deactivate":
                if (canDeactivate) {
                  setShowModal(true);
                } else {
                  setShowErrorModal(true);
                }
                break;
              case "activate":
                setShowModal(true);
                break;
            }
          }}
        >
          <MenuSection>
            {!isAlreadyOnEditPage && (
              <MenuItem id="edit" testID="edit-action">
                <Text style="input/text-field">{t("Edit")}</Text>
              </MenuItem>
            )}
            {isActive ? (
              <MenuItem id="deactivate" testID="deactivate-action">
                <Text color="text-danger" style="input/text-field">
                  {t("Deactivate")}
                </Text>
              </MenuItem>
            ) : (
              <MenuItem id="activate" testID="activate-action">
                <Text style="input/text-field">{t("Activate")}</Text>
              </MenuItem>
            )}
          </MenuSection>
        </DropdownMenu>
      )}
      <ReactivateDeactivateModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        templateId={templateId}
        name={name}
        isActive={isActive}
        mutate={mutate}
        apiPathPrefix={apiPathPrefix}
      />
      <Modal
        testID="cannot-deactivate-modal"
        isOpen={showErrorModal}
        onOpenChange={() => setShowErrorModal(false)}
      >
        <Dialog>
          <DialogHeader onClose={() => setShowErrorModal(false)}>
            {t("Cannot deactivate template")}
          </DialogHeader>
          <DialogContent>
            <Text>
              {isInActivePackage
                ? t(
                    'The template "{{name}}" cannot be deactivated because it is currently being used in one or more active packages. Please remove it from all packages before deactivating.',
                    { name }
                  )
                : t(
                    'The template "{{name}}" cannot be deactivated because it is the last active template. You must have at least one active template.',
                    { name }
                  )}
            </Text>
          </DialogContent>
          <DialogFooter>
            <PrimaryButton
              onPress={() => setShowErrorModal(false)}
              testID="ok-button"
            >
              {t("OK")}
            </PrimaryButton>
          </DialogFooter>
        </Dialog>
      </Modal>
    </>
  );
}
